import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowRight } from 'lucide-react';
import { AuthContext } from '../App';
import { authApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: 'Missing fields', description: 'Enter both email and password.' });
      return;
    }
    setLoading(true);
    try {
      const r = await authApi.login({ email, password });
      login(r.data.user, r.data.token);
      toast({ title: 'Welcome back', description: 'Locked and loaded.' });
      navigate('/app');
    } catch (err) {
      toast({ title: 'Login failed', description: err.response?.data?.detail || 'Invalid credentials' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-[#ffffff] border-r border-stone-200/80 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-50"></div>
        <div className="absolute inset-0 bg-grid opacity-30"></div>
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider text-stone-900">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-3">Mission Briefing</div>
            <h2 className="font-display text-4xl text-stone-900 leading-tight max-w-md">
              "Discipline is the bridge between strategy and approval."
            </h2>
            <div className="mt-6 flex items-center gap-3 text-sm text-stone-500">
              <div className="w-10 h-px bg-[#4b5320]"></div>
              Infantry Operating Doctrine
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#f7f6f0]">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>

          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-3">Sign In</div>
          <h1 className="font-display text-3xl mb-2">Welcome back, operator.</h1>
          <p className="text-stone-700 text-sm mb-10">Enter your credentials to continue your mission.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-stone-700 text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-stone-700 text-xs uppercase tracking-wider">Password</Label>
                <button type="button" className="text-xs text-[#4b5320] hover:text-[#6b7a3f]">Forgot?</button>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-[#fafaf6] border-stone-200 rounded-sm h-11 text-stone-900" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-11">
              {loading ? 'Signing in...' : 'Sign in'} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-stone-200/80 text-sm text-stone-500">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#4b5320] hover:text-[#6b7a3f]">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
